import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Clock, Wrench, CheckCircle, ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("dd_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const STATUS_META = {
  new_lead: { label: "New Leads", color: "#3B82F6", icon: Briefcase },
  scheduled: { label: "Scheduled", color: "#F59E0B", icon: Clock },
  in_progress: { label: "In Progress", color: "#D95D39", icon: Wrench },
  completed: { label: "Completed", color: "#16A34A", icon: CheckCircle },
};

export default function DashboardHome() {
  const [summary, setSummary] = useState({});
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, jobsRes] = await Promise.all([
        axios.get(`${API}/jobs/summary`, authHeaders()),
        axios.get(`${API}/jobs`, authHeaders()),
      ]);
      setSummary(sumRes.data);
      setJobs(jobsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = jobs.filter((j) => {
    const matchText = !filter || j.name.toLowerCase().includes(filter.toLowerCase()) || j.phone.includes(filter) || j.animal_type.toLowerCase().includes(filter.toLowerCase());
    const matchStatus = statusFilter === "all" || j.status === statusFilter;
    return matchText && matchStatus;
  });

  return (
    <div className="p-6 sm:p-8" data-testid="dashboard-home">
      <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-6">Job Overview</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="summary-cards">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <button key={key} onClick={() => setStatusFilter(statusFilter === key ? "all" : key)} data-testid={`summary-${key}`} className={`border rounded-sm p-5 text-left transition-colors duration-150 ${statusFilter === key ? "border-[" + meta.color + "] bg-[" + meta.color + "]/5" : "border-[#1F2937] bg-[#0F1A15] hover:border-[#374151]"}`}>
            <meta.icon className="w-5 h-5 mb-2" style={{ color: meta.color }} />
            <p className="text-[#F8FAFC] font-black text-2xl">{summary[key] || 0}</p>
            <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">{meta.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input data-testid="job-search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by name, phone, animal..." className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] pl-10 h-10 rounded-sm focus-visible:ring-[#D95D39]" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter" className="bg-[#0F1A15] border border-[#1F2937] text-[#F8FAFC] rounded-sm h-10 px-3 text-sm focus:ring-[#D95D39] focus:outline-none">
          <option value="all">All Status</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      {/* Job List */}
      <div className="border border-[#1F2937] rounded-sm overflow-hidden" data-testid="job-list">
        <div className="grid grid-cols-[1fr_1fr_1fr_120px_100px_60px] gap-2 px-4 py-3 bg-[#0F1A15] border-b border-[#1F2937] text-[#64748B] text-xs font-bold uppercase tracking-wider">
          <span>Customer</span><span>Address</span><span>Animal</span><span>Urgency</span><span>Status</span><span></span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] text-sm">No jobs found</div>
        ) : filtered.map((job) => {
          const meta = STATUS_META[job.status] || STATUS_META.new_lead;
          return (
            <Link key={job.id} to={`/dashboard/jobs/${job.id}`} data-testid={`job-row-${job.id}`} className="grid grid-cols-[1fr_1fr_1fr_120px_100px_60px] gap-2 px-4 py-3 border-b border-[#1F2937] hover:bg-[#0F1A15]/50 transition-colors duration-150 items-center">
              <div>
                <p className="text-[#F8FAFC] text-sm font-semibold truncate">{job.name}</p>
                <p className="text-[#64748B] text-xs truncate">{job.phone}</p>
              </div>
              <p className="text-[#9CA3AF] text-sm truncate">{job.address}</p>
              <p className="text-[#9CA3AF] text-sm">{job.animal_type}</p>
              <span className={`text-xs font-semibold uppercase px-2 py-1 rounded-sm w-fit ${job.urgency === "emergency" ? "bg-[#DC2626]/10 text-[#DC2626]" : job.urgency === "urgent" ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#1E293B] text-[#9CA3AF]"}`}>{job.urgency}</span>
              <span className="text-xs font-bold px-2 py-1 rounded-sm w-fit" style={{ backgroundColor: meta.color + "15", color: meta.color }}>{meta.label}</span>
              <ArrowRight className="w-4 h-4 text-[#64748B]" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
